import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { supabase } from '@/lib/supabase';
import { hasAtLeast, type Tier } from '@/lib/subscription';

/* ── Coach Roster Export ── */

export interface RosterClient {
  id: string;
  handle: string | null;
  display_name: string | null;
  lifting_score: number | null;
  last_workout_at: string | null;
}

export async function exportCoachRoster(coachId: string, clients: RosterClient[]): Promise<void> {
  const rows: string[] = ['handle,display_name,strength_score,last_workout_date'];

  for (const cl of clients) {
    const handle = (cl.handle ?? '').replace(/"/g, '""');
    const displayName = (cl.display_name ?? '').replace(/"/g, '""');
    const score = cl.lifting_score ?? 0;
    const lastDate = cl.last_workout_at
      ? new Date(cl.last_workout_at).toISOString().slice(0, 10)
      : '';
    rows.push(`"${handle}","${displayName}",${score},${lastDate}`);
  }

  const csv = rows.join('\n');

  if (Platform.OS === 'web') {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'duel_coach_roster.csv';
    a.click();
    URL.revokeObjectURL(url);
    return;
  }

  const fileUri = (FileSystem.cacheDirectory ?? FileSystem.documentDirectory ?? '') + 'duel_coach_roster.csv';
  await FileSystem.writeAsStringAsync(fileUri, csv, { encoding: 'utf8' });

  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(fileUri, {
      mimeType: 'text/csv',
      dialogTitle: 'Export Coach Roster',
      UTI: 'public.comma-separated-values-text',
    });
  } else {
    throw new Error('Sharing is not available on this device');
  }
}

/**
 * Export workout data as CSV. Requires Pro tier or above.
 * @param tier - The user's current subscription tier (pass from useSubscription hook).
 */
export async function exportWorkoutData(userId: string, tier?: Tier): Promise<void> {
  // Gate: Personal data export requires Pro
  const effectiveTier = tier ?? 'free';
  if (!hasAtLeast(effectiveTier, 'pro')) {
    throw new Error('Data export requires a Pro subscription. Upgrade to export your workout history.');
  }
  // Fetch workouts
  const { data: workouts, error: wErr } = await supabase
    .from('workouts')
    .select('id, performed_at, payload')
    .eq('user_id', userId)
    .order('performed_at', { ascending: false });

  if (wErr) throw new Error(`Failed to fetch workouts: ${wErr.message}`);

  // Build CSV
  const rows: string[] = [
    'workout_date,exercise,set_number,weight,weight_unit,reps,rpe,notes',
  ];

  for (const w of workouts ?? []) {
    const pl =
      typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
    const date = w.performed_at
      ? new Date(w.performed_at).toISOString().slice(0, 10)
      : '';
    for (const ex of pl?.exercises ?? []) {
      const name = (ex.name ?? '').replace(/"/g, '""');
      const notes = (ex.notes ?? '').replace(/"/g, '""');
      const rpe = ex.rpe ?? '';
      for (let i = 0; i < (ex.sets ?? []).length; i++) {
        const s = ex.sets[i];
        rows.push(
          `${date},"${name}",${i + 1},${s.weight ?? 0},${pl?.units ?? 'lbs'},${s.reps ?? 0},${rpe},"${notes}"`,
        );
      }
    }
  }

  const csv = rows.join('\n');

  if (Platform.OS === 'web') {
    // Web: download via blob
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'duel_workouts.csv';
    a.click();
    URL.revokeObjectURL(url);
    return;
  }

  // Native: write to file and share
  const fileUri = (FileSystem.cacheDirectory ?? FileSystem.documentDirectory ?? '') + 'duel_workouts.csv';
  await FileSystem.writeAsStringAsync(fileUri, csv, {
    encoding: 'utf8',
  });

  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(fileUri, {
      mimeType: 'text/csv',
      dialogTitle: 'Export Workout Data',
      UTI: 'public.comma-separated-values-text',
    });
  } else {
    throw new Error('Sharing is not available on this device');
  }
}
